/**
 * Fetches the customer-set internal→external role ID mappings for a location
 * so the `role_mapping_cache` IndexedDB store (API-4674) can be populated for
 * offline `externalRoleId` resolution.
 *
 * Mirrors `ldrApi.ts`'s resilience (up to `MAX_RETRIES` retries and
 * `fetchWithNetworkReporting`, so a network failure flips the terminal offline
 * immediately). The endpoint returns a slim mapped-only list in a single,
 * unpaginated response, so this makes one request and reads `data` directly.
 *
 * The result is normalized to a `Record<number, string>` (internalRoleId →
 * externalRoleId) for O(1) lookup at punch time.
 */

import { env } from '@src/config/env';
import { fetchWithNetworkReporting } from './accountApi';

const MAX_RETRIES = 2;

// NOTE: this endpoint (`terminal_role_mapping`) returns snake_case, unlike its
// integration sibling `/integrations/{app}/time_punches/{id}` (`enriched_punch`)
// which returns camelCase. Do not "correct" these to camelCase — a mismatch
// here silently drops every row and yields an empty `role_mapping_cache`,
// leaving offline punches with a null `externalRoleId` (API-4674).
interface ApiRoleMapping {
    internal_role_id: number;
    external_role_id: string;
}

interface RoleMappingsResponse {
    data: ApiRoleMapping[];
}

/** Internal role ID → partner external role ID. */
export type RoleMappings = Record<number, string>;

async function withRetry<T>(fn: () => Promise<T>): Promise<T> {
    let lastErr: unknown;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
            return await fn();
        } catch (err) {
            lastErr = err;
        }
    }
    throw lastErr;
}

/**
 * Fetches the mapped-only role mappings for a company/location/integration and
 * normalizes them into a `Record<number, string>`. Rows missing an internal
 * role or external ID are skipped defensively (the endpoint already returns
 * mapped-only).
 */
export async function fetchRoleMappings(
    companyId: number,
    locationId: number,
    applicationName: string,
    accessToken: string
): Promise<RoleMappings> {
    const rows = await withRetry(async () => {
        const url = new URL(
            `${env.apiBaseUrl}/api/v2/company/${companyId}/integrations/${encodeURIComponent(
                applicationName
            )}/role_mappings`
        );
        url.searchParams.set('location_id', String(locationId));

        const res = await fetchWithNetworkReporting(url.toString(), {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                Accept: 'application/json'
            }
        });

        if (!res.ok) {
            const body = await res.text().catch(() => '');
            throw new Error(
                `Role mappings fetch failed (${res.status}): ${body || res.statusText}`
            );
        }

        const json: RoleMappingsResponse = await res.json();
        return json.data ?? [];
    });

    const mappings: RoleMappings = {};
    for (const row of rows) {
        if (row.internal_role_id != null && row.external_role_id) {
            mappings[row.internal_role_id] = row.external_role_id;
        }
    }
    return mappings;
}
